import {
    Box,
    Button,
    Grid,
    Skeleton,
    Theme,
    Typography
} from "@mui/material";
import { checkImageExists, toINR } from "../../../../utils/utilityFunctions";
import { ColorMap } from "../../types";
import { BlackDownArrowIcon } from "../../../../assets/SvgIcons";
import { useSelector } from "react-redux";
import { RootStateType } from "../../../../redux-store/reducers";

const colorMap: ColorMap = {
    Successful: 'success.500',
    "In Progress": 'warning.500',
    Failed: 'error.500',
    "S": 'success.500',
    "F": 'error.500',
    "P": 'warning.500'
}

const otpStatusMap: Record<string, string> = {
    "S": "Success",
    "F": "Failed",
    "P": "Pending"
}

function ColorCircle(
    { backgroundColor }: { backgroundColor: string }
): JSX.Element {
    return (
        <Box
            sx={{
                width: "9px",
                height: "9px",
                borderRadius: "50%",
                backgroundColor
            }}
        />
    )
}

export default function OrderInfo(
    {
        data,
        handleViewDetailsClick,
        isLoading
    }: any
): JSX.Element {

    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);


    return (
        <Box>
            <Box
                sx={(theme: Theme) => ({
                    paddingBottom: "15px",
                    borderBottom: `0.5px dashed ${theme.palette.text.secondary}B2`
                })}
            >

                {isLoading ? (
                    <Skeleton variant="text" animation="wave" />
                ) : (
                    <Typography
                        variant="body_500"
                        sx={(theme: Theme) => ({
                            color: theme.palette.text.secondary
                        })}
                    >
                        {data.orderDate}
                    </Typography>
                )}
            </Box>
            {/* data?.schemes is an array of array */}
            {(data?.schemes ? data?.schemes : [[], []])?.map((arrayObj: Array<any>, key: number) => (
                <Box
                    key={key}
                    sx={(theme: Theme) => ({
                        borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                    })}
                >
                    {arrayObj.map((obj: any, idx: number) => (
                        <Grid
                            container
                            key={idx}
                            spacing={{ xs: 1.5, md: 2 }}
                            sx={{
                                py: { xs: 2, md: 3 },
                                alignItems: "center",
                            }}
                        >
                            {/* LEFT SECTION — SCHEME INFO */}
                            <Grid item xs={12} md={2}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        rowGap: { xs: "10px", md: "15px" },
                                    }}
                                >
                                    {/* From scheme */}
                                    <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                        {isLoading ? (
                                            <>
                                                <Skeleton variant="rectangular" width={35} height={35} />
                                                <Skeleton variant="text" width={120} />
                                            </>
                                        ) : (
                                            <>
                                                <img
                                                    src={
                                                        checkImageExists(`${obj?.amcId}`)
                                                            ? `/images/amcIcons/${obj?.amcId}.svg`
                                                            : `/images/amcIcons/default.svg`
                                                    }
                                                    alt={obj?.fromSchemeName}
                                                    width={35}
                                                    height={35}
                                                />
                                                <Typography variant="overline_400">
                                                    {obj?.fromSchemeName}
                                                </Typography>
                                            </>
                                        )}
                                    </Box>

                                    {/* Down arrow + To scheme */}
                                    {!isLoading && obj?.toSchemeName && (
                                        <>
                                            <Box sx={{ ml: "22px" }}>
                                                <BlackDownArrowIcon />
                                            </Box>

                                            <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                                <img
                                                    src={
                                                        checkImageExists(`${obj?.amcId}`)
                                                            ? `/images/amcIcons/${obj?.amcId}.svg`
                                                            : `/images/amcIcons/default.svg`
                                                    }
                                                    alt={obj?.toSchemeName}
                                                    width={35}
                                                    height={35}
                                                />
                                                <Typography variant="overline_400">
                                                    {obj?.toSchemeName}
                                                </Typography>
                                            </Box>
                                        </>
                                    )}
                                </Box>
                            </Grid>

                            {/* RIGHT SECTION — DETAILS GRID */}
                            <Grid item xs={12} md={8.5}>
                                <Grid
                                    container
                                    spacing={{ xs: 1.5, md: 2 }}
                                    justifyContent="flex-start"
                                    sx={{ flexWrap: "wrap" }}
                                >
                                    {/* Purchase Type */}
                                    <Grid
                                        item
                                        xs={6}
                                        md={3}
                                        sx={{
                                            minHeight: "55px",
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                        }}
                                    >
                                        {isLoading ? (
                                            <>
                                                <Skeleton variant="text" width="100%" />
                                                <Skeleton variant="text" width="80%" />
                                            </>
                                        ) : (
                                            <>
                                                <Typography
                                                    variant="body_400"
                                                    color="text.secondary"
                                                    sx={{ mb: 0.25 }}
                                                >
                                                    Purchase Type
                                                </Typography>
                                                <Typography variant="body_400" sx={{ fontWeight: 600 }}>
                                                    {obj.purchaseType}
                                                </Typography>
                                            </>
                                        )}
                                    </Grid>

                                    {/* Amount */}
                                    <Grid
                                        item
                                        xs={6}
                                        md={3}
                                        sx={{
                                            minHeight: "55px",
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                        }}
                                    >
                                        {isLoading ? (
                                            <>
                                                <Skeleton variant="text" width="100%" />
                                                <Skeleton variant="text" width="80%" />
                                            </>
                                        ) : (
                                            <>
                                                <Typography
                                                    variant="body_400"
                                                    color="text.secondary"
                                                    sx={{ mb: 0.25 }}
                                                >
                                                    Amount
                                                </Typography>
                                                <Typography variant="body_500" sx={{ fontWeight: 600 }}>
                                                    {obj.amount
                                                        ? toINR({ value: obj.amount, locale, currencyCode })
                                                        : "-"}
                                                </Typography>
                                            </>
                                        )}
                                    </Grid>

                                    {/* Units */}
                                    <Grid
                                        item
                                        xs={6}
                                        md={2}
                                        sx={{
                                            minHeight: "55px",
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                        }}
                                    >
                                        {isLoading ? (
                                            <>
                                                <Skeleton variant="text" width="100%" />
                                                <Skeleton variant="text" width="80%" />
                                            </>
                                        ) : (
                                            <>
                                                <Typography
                                                    variant="body_400"
                                                    color="text.secondary"
                                                    sx={{ mb: 0.25 }}
                                                >
                                                    Units
                                                </Typography>
                                                <Typography variant="body_500" sx={{ fontWeight: 600 }}>
                                                    {obj?.units || "-"}
                                                </Typography>
                                            </>
                                        )}
                                    </Grid>

                                    {/* OTP Status */}
                                    <Grid
                                        item
                                        xs={6}
                                        md={2}
                                        sx={{
                                            minHeight: "55px",
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                        }}
                                    >
                                        {isLoading ? (
                                            <>
                                                <Skeleton variant="text" width="100%" />
                                                <Skeleton variant="text" width="80%" />
                                            </>
                                        ) : (
                                            <>
                                                <Typography
                                                    variant="body_400"
                                                    color="text.secondary"
                                                    sx={{ mb: 0.25 }}
                                                >
                                                    OTP Status
                                                </Typography>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                    <ColorCircle
                                                        backgroundColor={
                                                            colorMap[obj.isOTPVerified || "F"]
                                                        }
                                                    />
                                                    <Typography variant="body_400" sx={{ fontWeight: 600 }}>
                                                        {otpStatusMap[obj?.isOTPVerified || "F"]}
                                                    </Typography>
                                                </Box>
                                            </>
                                        )}
                                    </Grid>

                                    {/* Status */}
                                    <Grid
                                        item
                                        xs={6}
                                        md={2}
                                        sx={{
                                            minHeight: "55px",
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                        }}
                                    >
                                        {isLoading ? (
                                            <>
                                                <Skeleton variant="rounded" width={12} height={12} />
                                                <Skeleton variant="text" width="80%" />
                                            </>
                                        ) : (
                                            <>
                                                <Typography
                                                    variant="body_400"
                                                    color="text.secondary"
                                                    sx={{ mb: 0.25 }}
                                                >
                                                    Status
                                                </Typography>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                    <ColorCircle
                                                        backgroundColor={colorMap[obj.status || "Failed"]}
                                                    />
                                                    <Typography variant="body_400" sx={{ fontWeight: 600 }}>
                                                        {obj?.status || "Failed"}
                                                    </Typography>
                                                </Box>
                                            </>
                                        )}
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* BUTTON */}
                            <Grid
                                item
                                xs={12}
                                md={1.5}
                                sx={{
                                    display: "flex",
                                    justifyContent: { xs: "flex-start", md: "flex-end" },
                                }}
                            >
                                {isLoading ? (
                                    <Skeleton variant="rounded" width={100} height={40} />
                                ) : (
                                    <Button
                                        variant="outlined"
                                        sx={(theme: Theme) => ({
                                            color: theme.palette.primary[500],
                                            border: `1px solid ${theme.palette.primary[500]}`,
                                            borderRadius: "4px",
                                            padding: { xs: "6px", md: "5px" },
                                            width: "100%",
                                            ...theme.typography.body_500,
                                        })}
                                        onClick={() =>
                                            handleViewDetailsClick(
                                                data.orderId,
                                                obj.schemeId,
                                                obj?.transNo,
                                                obj?.id
                                            )
                                        }
                                    >
                                        View Details
                                    </Button>
                                )}
                            </Grid>
                        </Grid>
                    ))}
                </Box>
            ))}
        </Box>
    );
}